import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import _ from 'lodash';
import EmailButton from '../EmailButton';
import BaseComponent from '../../../baseClasses/BaseComponent';
import settingsIcon from '../../../assets/images/settings-icon-light.svg';
import projectsIcon from '../../../assets/images/projects-icon-light-gray.svg';
import LoadingDots from '../../../components/LoadingDots';
import LocationProjectHeader from '../PORProjectDocuments/LocationProjectHeader';
import TabbedPageControl from '../../../components/TabbedPageControl';
import AttachmentsTable from './AttachmentsTable';
import AttachmentDisplay from '../PORProjectDocuments/AttachmentsTable/AttachmentDisplay';
import dataModel from '../../../models/DataModel';
import DataSource from '../../../dao/DataSource/DataSource';
import CollectionDataSource from '../../../dao/DataSource/CollectionDataSource';
import ZipExportButton from '../ZipExportButton';
import progressReportsService from '../../../services/progressReportsService';
import { DOCUMENTS, CLOSEOUT_HISTORY, PROGRESS_REPORTS } from '../../../constants/TabConstants';
import './PORLocationDocuments.scss';
import LocationsIcon from '../../../components/Iconoclast/LocationsIcon';

const location_projects_with_documents = {
  projects: {
    all_locations: {
    },
    location: {
      
    },
  },
};

const project_in_locations = {
  locations: {
    additionalFields: ['documents', 'all_closeouts'],
    all_closeouts: { user: {} },
    documents: { user: {}, upload: {}, excludeFields: ['upload', 'upload_thumb', 'upload_small', 'upload_annotated', 'attachment', 'conversions', 'photo_latitude', 'photo_longitude', 'photo_taken_at', 'location_source'] },
  },
};

const attachmentDescriptor = { 
  attachment: { 
    user: {},
    additionalFields: ['upload', 'photo_longitude', 'photo_latitude', 'photo_taken_at'], 
  },
};

export class PORLocationDocuments extends BaseComponent {
  
  constructor(props) {
    super(props);
    this.state = {
      scrollHeightDiffFromHeader: 0,
      selectedTabName: DOCUMENTS,
      projects: [],
      progressReports: null, // null = not loaded yet, [] = loaded but empty
      selectedAttachment: null,
      initialized: false,
      loadingMore: true,
    };
    this.deleteCloseout = this.deleteCloseout.bind(this);
    this.deleteProgressReport = this.deleteProgressReport.bind(this);
    this.tabs = [DOCUMENTS, CLOSEOUT_HISTORY, PROGRESS_REPORTS];
  }

  handleProjectRetrieval(project, action, acData, fetchedData) {
    const isActionCableUpdate = acData != null;
    const hasProgressReports = this.state.progressReports != null; // null/undefined = not loaded, [] = loaded but empty
    
    // Skip unnecessary updates from ActionCable events (e.g., status_update from step completions)
    // These don't change data we display on the progress reports page
    if (isActionCableUpdate) {
      // Only update project state if it actually changed (avoid unnecessary re-renders)
      if (this.state.project && this.state.project.id === project.id) {
        return; // Same project, skip update to prevent flickering
      }
    }
    
    this.setState({ project, initialized: action !== 'prefetch' ? true : this.state.initialized });
    
    // Only initialize locations data source on initial load or explicit refresh, not ActionCable updates
    if (action !== 'prefetch' && !isActionCableUpdate) {
      this.handleInitializeLocationsDataSource(project.id);
    }
    
    // Only fetch progress reports on initial load, not on ActionCable updates.
    // ActionCable updates for completion steps don't affect progress reports.
    // Note: Progress report generation triggers a separate notification banner, not this callback.
    if (project && project.id && !isActionCableUpdate && !hasProgressReports) {
      this.handleFetchProgressReportsForProject(project.id);
    }
  }

  handleAttachmentRetrieval(attachment, action, acData, fetchedData) {
    if (!attachment) {
      dataModel.showGlobalModal('SYSTEM_POPUP', {
        message: 'The document you selected is no longer available.',
        captionText: 'ERROR',
        type: 'warning',
        oneButton: true,
        height: 163,
        width: 320,
        okButtonText: 'OK',
      });
    }
    else {
      this.setState({ selectedAttachment: attachment });
    }
  }

  handleLocationsRetrieval(locations, action, acData, fetchedData) {
    this.setState({
      locations: locations.locations,
      loadingMore: !(action != 'prefetch') });
  }

  handleCloseoutRetrieval(closeout, action, acData, fetchedData) {
    if (!closeout) {
      dataModel.showGlobalModal('SYSTEM_POPUP', {
        message: 'The document you selected is no longer available.',
        captionText: 'ERROR',
        type: 'warning',
        oneButton: true,
        height: 163,
        width: 320,
        okButtonText: 'OK',
      });
    }
    else {
      this.setState({ selectedAttachment: closeout });
    }
  }

  handleInitializeProjectDataSource(project_id) {
    if (this.projectDataSource) {
      this.projectDataSource.release();
    }
    this.setState({ accessDenied: false });
    this.projectDataSource = new DataSource('Project', project_id, location_projects_with_documents,
      this.handleProjectRetrieval, { include_location_documents: true });
    // The API returns 403 when a standard user is not assigned to this project (hand-typed URL).
    // Without the catch the rejection from the DAO is unhandled and the page sits on a blank frame.
    this.projectDataSource.get().catch(() => {
      if (!this.projectDataSource) {
        return;
      }
      this.setState({ accessDenied: true, project: null, loadingMore: false, initialized: true });
    });
  }

  handleInitializeLocationsDataSource(projectId) {
    this.setState({ loadingMore: true });
    this.locationsDataSource = new CollectionDataSource('ProjectLocationsCollection', 'Location', project_in_locations,
      this.handleLocationsRetrieval, 'ProjectLocations', projectId, this.props.currentUser.id, this.props.currentCompany.id, projectId);
    this.locationsDataSource.current(null, null, true);
  }
  
  handleInitializeAttachmentDataSource(attachment_id) {
    if (this.attachmentDataSource) {
      this.attachmentDataSource.release();
    }
    this.attachmentDataSource = new DataSource('Attachment', attachment_id, attachmentDescriptor, this.handleAttachmentRetrieval);
    this.attachmentDataSource.get();
  }
  
  handleInitializeCloseoutDataSource(closeout_id) {
    if (this.closeoutDataSource) {
      this.closeoutDataSource.release();
    }
    this.closeoutDataSource = new DataSource('Closeout', closeout_id, attachmentDescriptor, this.handleCloseoutRetrieval);
    this.closeoutDataSource.get();
  }
  
  componentDidMount() {
    const project_id = this.props.params.project_id;
    const location_id = this.props.params.location_id;
    const document_id = this.props.params.document_id;
    const closeout_id = this.props.params.closeout_id;
    const progress_report_id = this.props.params.progress_report_id;
    
    // Check for tab query parameter
    const search = this.props.location ? this.props.location.search : window.location.search;
    const searchParams = new URLSearchParams(search);
    const tabParam = searchParams.get('tab');

    if (project_id) {
      this.handleInitializeProjectDataSource(project_id);
    }
    if (location_id && location_id != 'all') {
      this.setState({ selectedLocationId: location_id });
    }
    if (document_id) {
      this.setState({ selectedTabName: 'documents' });
      this.handleInitializeAttachmentDataSource(document_id);
    }
    else if (closeout_id) {
      this.setState({ selectedTabName: 'closeout history' });
      this.handleInitializeCloseoutDataSource(closeout_id);
    }
    else if (progress_report_id) {
      this.setState({ selectedTabName: PROGRESS_REPORTS });
      this.handleFetchProgressReport(progress_report_id);
    }
    else if (tabParam === 'progress_reports') {
      this.setState({ selectedTabName: PROGRESS_REPORTS });
    }
    else if (tabParam === 'closeouts') {
      this.setState({ selectedTabName: CLOSEOUT_HISTORY });
    }
  }

  componentDidUpdate(prevProps) {
    const location_id = this.props.params.location_id;
    const project_id = this.props.params.project_id;
    const document_id = this.props.params.document_id;
    const closeout_id = this.props.params.closeout_id;

    if (project_id && (project_id != prevProps.params.project_id)) {
      this.handleInitializeProjectDataSource(project_id);
      this.setState({ selectedAttachment: null, selectedAttachments: null });
    }
    else {
      if (location_id != prevProps.params.location_id) {
        this.setState({ selectedLocationId: location_id != 'all' ? location_id : null, selectedAttachments: null });
      }
      if (document_id || closeout_id) {
        if (document_id && document_id != this.state.document_id) {
          this.setState({ location_id, project_id, document_id, closeout_id });
          this.handleInitializeAttachmentDataSource(document_id);
        }
        else if (closeout_id && closeout_id != this.state.closeout_id) {
          this.setState({ location_id, project_id, document_id, closeout_id });
          this.handleFetchCloseout(closeout_id);
        }
      }
    }
  }

  componentWillUnmount() {
    if (this.projectDataSource) {
      this.projectDataSource.release();
      this.projectDataSource = null;
    }
    if (this.attachmentDataSource) {
      this.attachmentDataSource.release();
      this.attachmentDataSource = null;
    }
    if (this.closeoutDataSource) {
      this.closeoutDataSource.release();
      this.closeoutDataSource = null;
    }
  }

  async handleFetchCloseout(closeout_id) {
    const { project } = this.state;
    let selectedAttachment;
    if (closeout_id) {
      let closeout = project.all_closeouts.find(c => c.id == closeout_id);
      if (!closeout) {
        this.handleInitializeCloseoutDataSource();
      }
      else {
        closeout = project.all_closeouts.find(c => c.id == closeout_id);
        
        selectedAttachment = closeout;
        if (!selectedAttachment) {
          dataModel.showGlobalModal('SYSTEM_POPUP', {
            message: 'The document you selected is no longer available.',
            captionText: 'ERROR',
            type: 'warning',
            oneButton: true,
            height: 163,
            width: 320,
            okButtonText: 'OK',
          });
        }
        else {
          this.setState({ selectedAttachment });
        }
      }
    }
  }

  handleSelectForExport(selectedProjects, selectedAttachments) {
    if (selectedAttachments && !_.isEmpty(selectedAttachments)) {
      this.setState({ enableExport: true, selectedAttachments });
    }
    else {
      this.setState({ enableExport: false, selectedAttachments: {} });
    }
  }
  
  deleteCloseout(closeoutId) {
    const { location_id, project_id } = this.props.match.params;
    let { project, selectedAttachment } = this.state;
    let viewingAllProjects = false;
    if (!project) {
      viewingAllProjects = true;
      project = _.cloneDeep(this.state.locationProjects.projects.find(p => p.id == selectedAttachment.project_id));
    }
    const closeoutIndex = project.all_closeouts.findIndex(c => c.id == closeoutId);
    const deletedCloseout = project.all_closeouts.splice(closeoutIndex, 1)[0];
    if (!viewingAllProjects) {
      this.setState({ project });
    }
    this.props.updateProject(project);
    this.props.history.push('/por/project/' + project_id + '/location/all?tab=closeouts');
    dataModel.closeoutsDAO.deleteCloseout(closeoutId)
      .catch(error => {
        project.all_closeouts.splice(closeoutIndex, 0, deletedCloseout);
        if (!viewingAllProjects) {
          this.setState({ project });
        }
        this.props.updateProject(project);
      });
  }

  deleteProgressReport(progressReportId) {
    const { location_id, project_id } = this.props.match.params;
    progressReportsService.deleteProgressReportOptimistic({
      progressReportId,
      progressReports: this.state.progressReports,
      setState: this.setState.bind(this),
      history: this.props.history,
      location_id,
      project_id,
    });
  }

  async handleFetchProgressReportsForProject(projectId) {
    if (!projectId) return;

    const response = await progressReportsService.getProgressReportsByProject(projectId);
    const progressReports = (response && !response.error && response.progressReports)
      ? response.progressReports
      : [];
    this.setState({ progressReports });
  }

  async handleFetchProgressReport(progress_report_id) {
    const { progressReports } = this.state;
    let selectedAttachment = progressReports && progressReports.find(pr => pr.id == progress_report_id);
    
    if (!selectedAttachment) {
      const response = await progressReportsService.getProgressReport(progress_report_id);
      if (response && !response.error) {
        selectedAttachment = response.progressReport;
      }
    }
    
    if (selectedAttachment) {
      this.setState({ selectedAttachment });
    } else {
      dataModel.showGlobalModal('SYSTEM_POPUP', {
        message: 'The Progress Report you selected is no longer available.',
        captionText: 'ERROR',
        type: 'warning',
        oneButton: true,
        height: 163,
        width: 320,
        okButtonText: 'OK',
      });
    }
  }

  handleSubHeaderExpand(expandAmount, className, animatorGroup, time) {
    if (animatorGroup) {
      const startHeight = window.innerHeight - this.state.scrollHeightDiffFromHeader;
      const endHeight = window.innerHeight - 18;
      const ani1 = this.animator.createPropertyAnimation(className, time, {
        height: { startVal: startHeight, endVal: endHeight },
      });
      animatorGroup.push(ani1);
    } else {
      this.setState({ scrollHeightDiffFromHeader: expandAmount });
    }
  }

  handleExpansion(height) {
    this.setState({ displayHeight: height });
    this.handleSubHeaderExpand(height, 'documents-detail-container');
    if (this.props.handleMapExpanded) {
      this.props.handleMapExpanded(height > 0 ? true : false);
    }
  }

  handleEmptyExpansion(height) {
    this.setState({ displayHeight: height });
    this.handleSubHeaderExpand(height, 'no-documents-container');
    if (this.props.handleMapExpanded) {
      this.props.handleMapExpanded(height > 0 ? true : false);
    }
  }

  handleSelectAttachment(attachment) {
    this.setState({ selectedAttachment: attachment });
  }

  handleTabClick(tabName) {    
    this.switchToTab(tabName);
  }

  switchToTab(tabName) {
    if (this.state.selectedTabName != tabName) {
      this.viewKey = Math.floor(Math.random() * 100);

      this.setState({
        selectedTabName: tabName, toggleSelected: !this.state.toggleSelected,
        selectedAttachments: null, selectedProjects: null, selectedAttachment: null,
      });
    }
  }

  handleDownload() {
    this.setState({ toggleSelected: !this.state.toggleSelected });
    setTimeout(() => {
      this.setState({ selectedAttachments: null, selectedProjects: null });
    }, 100);
  }

  handleGoToProject(project) {
    this.props.history.push('/projects/' + project.id);
  }

  handleNavigateToLocation(location) {
    if (location && location.id) {
      this.props.history.push('/por/location/' + location.id + '/project/all');
    }
  }

  render() {
    const { mapExpanded } = this.props;
    const { project_id, location_id, document_id, closeout_id } = this.props.params;
    const { project, scrollHeightDiffFromHeader, selectedTabName, loadingMore, accessDenied } = this.state;

    if (accessDenied) {
      return (
        <div className="project-documents-container" key="no-access">
          <div className="no-documents-container" style={{ top: scrollHeightDiffFromHeader + 61 }}>
            <img className="no-documents-icon" alt="no-access" src={projectsIcon} />
            <div className="no-documents-text">You do not have access to this project.<br />Select a project from the list to view its documents.</div>
          </div>
        </div>
      );
    }

    if (!project) {
      return null;
    }
    const style = { top: scrollHeightDiffFromHeader + 61 };
    let mapDisplayed = '';
    if (scrollHeightDiffFromHeader > 0) {
      mapDisplayed = '--map-displayed';
    }

    let loadingDots = null;
    if (loadingMore) {
      loadingDots = <LoadingDots />;
    }

    const locations = this.state.locations ? this.state.locations : [];
    let zipButton = null;
    let emailButton = null;
    let attachmentTable = null;
    let attachmentPreview = null;
    if (!loadingMore) {
      if (
        this.state.initialized &&
        !locations &&
        !location_id &&
        (location_id == 'all' && locations.filter(c => {
          return (
            (c.documents && c.documents.length > 0) ||
            (c.all_closeouts && c.all_closeouts.length > 0)
          );
        }).length == 0)
      ) {
        let emptyState;
        if (!location_id) {
          emptyState = (
            <div className="no-documents-container" style={style}>
              <img className="no-documents-icon" alt="no-docs" src={projectsIcon} />
              <div className="no-projects-text">To view Closeouts and Documents,<br />select a Location or the All Locations option.</div>
            </div>
          );
        }
        else if (locations && locations.length > 0) {
          emptyState = (
            <div className="no-documents-container" style={style}>
              <img className="no-documents-icon" alt="no-docs" src={settingsIcon} />
              <div className="no-documents-text">No Documents found.</div>
            </div>
          );
        }
        else {
          emptyState = (
            <div className="no-documents-container" style={style}>
              <img className="no-documents-icon" alt="no-docs" src={LocationsIcon} />
              <div className="no-documents-text">No Locations found for this project.</div>
            </div>
          );
        }

        return (
          <div className="project-documents-container" key="no-documents">
            <LocationProjectHeader
              displayingAll={project_id == 'all'}
              project={project}
              location={location}
              handleExpansion={this.handleEmptyExpansion}
              closeDropdown={this.props.handleCloseDropdown}
              mapExpanded={mapExpanded}
            />
            {emptyState}
          </div>
        );
      } else {
        const { selectedAttachments, selectedLocationId } = this.state;
        
        attachmentTable = (
          <AttachmentsTable
            locations={locations}
            selectedLocationId={selectedLocationId}
            progressReports={this.state.progressReports}
            selectedAttachmentId={document_id ? document_id : closeout_id}
            handleSelectAttachment={this.handleSelectAttachment}
            fileName={location.name}
            viewing={selectedTabName}
            currentCompany={this.props.currentCompany}
            handleSelectForExport={this.handleSelectForExport}
            toggleSelected={this.state.toggleSelected}
            gotoProject={this.handleGoToProject}
            gotoLocation={this.handleNavigateToLocation}
            initialized={this.state.initialized}
          />
        );
        attachmentPreview =
          <AttachmentDisplay
            key={this.state.selectedAttachment ? this.state.selectedAttachment.id : 'all'}
            attachment={this.state.selectedAttachment}
            fromCloseoutDocument={this.props.fromCloseoutDocument}
            handleDeleteCloseout={this.deleteCloseout}
            handleDeleteProgressReport={this.deleteProgressReport}
            isProgressReport={selectedTabName == PROGRESS_REPORTS}
          />;
          
        if (selectedAttachments && !_.isEmpty(selectedAttachments)) {
          let fileName = project.name;
          let filenameWhich = null;
          let which = null;

          var selectedAttachment=[];
          let nameSingleFile='';
          for(let selectedFiles in selectedAttachments) {    
            if(selectedFiles) {
              selectedAttachment.push(selectedFiles);   
            }
                   
          }

          if (selectedAttachment.length == 1) {
            for (const [key, value] of Object.entries(selectedAttachments)) {
              if(key && value){
                let name = value.name.split(/\.(?=[\w])/);
                nameSingleFile = name[0];
              }
              
            }
          }else{nameSingleFile='';}

          if (selectedTabName === DOCUMENTS) {
            filenameWhich = selectedAttachment.length > 1 ? fileName + '-uploads' : nameSingleFile;
            which = 'attachments';
          } else if (selectedTabName === PROGRESS_REPORTS) {
            filenameWhich = selectedAttachment.length > 1 ? fileName + '-progress-reports' : nameSingleFile;
            which = 'closeouts';
            // queryParamName='progressReportId' so backend receives progressReportId=... instead of closeouts=...
          } else {
            filenameWhich = selectedAttachment.length > 1 ? fileName + '-closeouts' : nameSingleFile;
            which = 'closeouts';
          }
          let attachmentIds = [];
          let attachmentNames = [];
          for (let attachmentId in selectedAttachments) {
            if (attachmentId) {
              if (!attachmentIds.find(id => { return attachmentId == id; })) {
                attachmentIds.push(attachmentId);    
                attachmentNames.push(selectedAttachments[attachmentId].name);
              }
            }
          }
          const zipQueryParamName = selectedTabName == PROGRESS_REPORTS ? 'progressReportId' : null;
          zipButton = (
            <div className="export-button-container">
              <ZipExportButton enabled={true} which={which} attachments={attachmentIds} filename={`${filenameWhich}`}
              queryParamName={zipQueryParamName}
              selectedAttachments={selectedAttachments}  selectedProjects={this.state.selectedProjects} 
                handleDownload={this.handleDownload}  tab={this.state.selectedTabName} popupUbication={'POR'}/>
            </div>
          );
        }
        else {
          zipButton = (
            <div className="export-button-container">
              <ZipExportButton enabled={false} />
            </div>
          );
        }

        if (selectedTabName === CLOSEOUT_HISTORY) {
          let attachmentIds = [];
          let attachmentNames = [];
          let attachmentCompany;
          let marketId = null;
          for (let attachmentId in selectedAttachments) {
            if (attachmentId) {
              if (!attachmentIds.find(id => { return attachmentId == id; })) {
                attachmentIds.push(attachmentId);
                attachmentNames.push(selectedAttachments[attachmentId].name);
              }
            }
          }
          if (selectedAttachments && Object.keys(selectedAttachments).length > 0) {
            let firstAttachment = selectedAttachments[Object.keys(selectedAttachments)[0]];
            attachmentCompany = firstAttachment.company_id;
          }
          if(this.state.locations.length > 0){
            marketId = this.state.locations[0].market_id;
          }
          
          if(marketId){
            emailButton = (
              <div className="email-button-container">
                <EmailButton enabled={attachmentIds.length > 0} attachments={attachmentIds} companyId={attachmentCompany}
                  marketId={marketId} handleUnselect={this.handleDownload}/>
              </div>
            );
          }
        }
      }
    }

    return (
      <div
        className="project-documents-container"
        key={
          'location-documents-' +
          (location ? location.id : 0)
        }
      >
        <LocationProjectHeader
          from={'projects'}
          displayingAll={!project}
          project={project}
          location={project.location}
          all_locations={project.all_locations}
          handleExpansion={this.handleExpansion}
          closeDropdown={this.props.handleCloseDropdown}
          mapExpanded={mapExpanded}
        />

        <div className={'documents-detail-container' + (mapDisplayed != '' ? (' documents-detail-container' + mapDisplayed) : '')} style={style}>
          <div className="tabs-container">
            <TabbedPageControl
              tabItems={this.tabs}
              selectedTabName={this.state.selectedTabName}
              handleClick={this.handleTabClick}
              borderTop={true}
            />
          </div>
          <div className={'documents-inner-container' + (mapDisplayed != '' ? (' documents-detail-container' + mapDisplayed) : '')}>
            <div className="attachments-table-container">
              {loadingDots}
              {zipButton}
              {emailButton}
              {attachmentTable}
            </div>

            <div className={'attachment-preview-container' + (mapDisplayed != '' ? (' attachment-preview-container' + mapDisplayed) : '')}>{attachmentPreview}</div>
          </div>
        </div>
      </div>
    );
  }
}
const mapStateToProps = (state, ownProps) => {
  return {
    inProgress: state.main.inProgress,
    currentCompany: state.new_companies.currentCompany,
    currentUser: state.new_users.currentUser,
  };
};
export default withRouter(connect(mapStateToProps)(PORLocationDocuments));




// WEBPACK FOOTER //
// ./src/containers/PORContainer/PORLocationDocuments/index.js